'use client'

import { isMobileVideoMotionPauseRuntime, isVideoHeavyActivityActive, subscribeVideoHeavyActivity } from '../videoPipelineProgress.js'

const MARGIN_PROFILES = Object.freeze({
  near100: '100px 0px 100px 0px',
  near50: '50px 0px 50px 0px',
})

// Render-managed scopes keep a bounded horizontal runway for nested carousels,
// while motion starts within 100/50px vertically. Static paint stays unchanged.
const MANAGED_MOTION_MARGIN_PROFILES = Object.freeze({
  near100: '100px 96px 100px 96px',
  near50: '50px 64px 50px 64px',
})

const EXIT_HYSTERESIS_MS = 90
const DISCONNECTED_SWEEP_MS = 1000
const CSS_MOTION_OWNER_STOP_ATTR = 'data-ql7-css-motion-owner-stopped'

const records = new WeakMap()
const nodes = new Set()
const observerPools = new Map()
const rootIds = new WeakMap()
const pendingExit = new WeakMap()
const runtimePausedWaapi = new WeakMap()
const datasetManagedScopes = new WeakSet()
const scopeLifecycleListeners = new Set()

let rootSeq = 0
let documentVisible = true
let reducedMotion = false
let videoHeavyActivityActive = isVideoHeavyActivityActive() && isMobileVideoMotionPauseRuntime()
let visibilityListener = null
let mediaQuery = null
let mediaQueryListener = null
let disconnectedSweepId = 0
let installed = false

const counters = {
  registrations: 0,
  unregistrations: 0,
  observerCallbacks: 0,
  observerEntries: 0,
  datasetWrites: 0,
  motionModeWrites: 0,
  animationStartRegistrations: 0,
  runtimePaused: 0,
  runtimeResumed: 0,
  cssStopped: 0,
  cssRestarted: 0,
  cssOwnerRegistrations: 0,
  cssOwnerPrunes: 0,
  cssOwnerNoopWritesAvoided: 0,
  loopRunning: 0,
  loopPaused: 0,
  duplicateChains: 0,
  startupScopeScanTargets: 0,
  forcedLayoutReads: 0,
  globalAnimationScans: 0,
  bodyMutationObservers: 0,
  descendantStateScans: 0,
  scopedAnimationScans: 0,
  animationTargetReconciles: 0,
  managedCssScanSkips: 0,
  managedAnimationTargetScanSkips: 0,
  disconnectedSweepRuns: 0,
  disconnectedSweepReleases: 0,
  disconnectedObserverReleases: 0,
}

function safeCall(fn, ...args) {
  try { return fn?.(...args) } catch { return undefined }
}

export function isIOSExchangeMotionContinuityRuntime(
  navigatorLike = typeof navigator !== 'undefined' ? navigator : null,
  pathname = typeof window !== 'undefined' ? window.location?.pathname : '',
) {
  const ua = String(navigatorLike?.userAgent || '')
  const platform = String(navigatorLike?.platform || '')
  const maxTouchPoints = Number(navigatorLike?.maxTouchPoints || 0)
  const ios = /iPhone|iPad|iPod/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1)
  if (!ios) return false
  return /(?:^|\/)exchange(?:\/|$)/i.test(String(pathname || ''))
}

function profileName(value) {
  return value === 'near50' ? 'near50' : 'near100'
}

function profileMargin(value, renderManaged = false) {
  const profiles = renderManaged ? MANAGED_MOTION_MARGIN_PROFILES : MARGIN_PROFILES
  return profiles[profileName(value)]
}

function rootKey(root) {
  if (!root) return 'viewport'
  let key = rootIds.get(root)
  if (!key) {
    key = `root-${++rootSeq}`
    rootIds.set(root, key)
  }
  return key
}

function resolveMarkedScrollRoot(node, requestedRoot = null) {
  if (requestedRoot instanceof Element) return requestedRoot
  if (!(node instanceof Element)) return null
  try {
    const root = node.parentElement?.closest?.('[data-ql7-visual-scroll-root="1"]')
    return root instanceof Element ? root : null
  } catch {
    return null
  }
}

function observerKey(root, profile, renderManaged = false) {
  return `${rootKey(root)}:${profileName(profile)}:${renderManaged ? 'managed-motion' : 'visual'}`
}

function cleanupPoolIfEmpty(pool) {
  if (!pool || pool.targets.size) return
  safeCall(() => pool.observer.disconnect())
  observerPools.delete(pool.key)
}

function setObservedNear(record, nextNear) {
  if (record.near === nextNear) return false
  record.near = nextNear
  return true
}

function scheduleObservedExit(node) {
  if (!(node instanceof Element)) return
  const oldTimer = pendingExit.get(node)
  if (oldTimer) clearTimeout(oldTimer)
  const timer = setTimeout(() => {
    pendingExit.delete(node)
    const current = records.get(node)
    if (!current || !current.node?.isConnected || !current.near) return
    if (setObservedNear(current, false)) applyRecordState(current)
  }, EXIT_HYSTERESIS_MS)
  pendingExit.set(node, timer)
}

function getObserver(root, profile, renderManaged = false) {
  if (typeof IntersectionObserver !== 'function') return null
  const key = observerKey(root, profile, renderManaged)
  const existing = observerPools.get(key)
  if (existing) return existing

  const observer = new IntersectionObserver((entries) => {
    counters.observerCallbacks += 1
    counters.observerEntries += entries.length
    for (const entry of entries) {
      const target = entry?.target
      if (!(target instanceof Element)) continue
      if (!target.isConnected) {
        counters.disconnectedObserverReleases += 1
        unregisterVisualScope(target)
        continue
      }
      const record = records.get(target)
      if (!record) continue
      const nextNear = !!entry.isIntersecting
      const firstObservation = record.firstIntersectionPending === true
      record.firstIntersectionPending = false

      if (nextNear) {
        const timer = pendingExit.get(record.node)
        if (timer) {
          clearTimeout(timer)
          pendingExit.delete(record.node)
        }
        if (setObservedNear(record, true)) applyRecordState(record)
        continue
      }

      if (firstObservation) {
        const oldTimer = pendingExit.get(record.node)
        if (oldTimer) clearTimeout(oldTimer)
        pendingExit.delete(record.node)
        if (setObservedNear(record, false)) applyRecordState(record)
        continue
      }

      scheduleObservedExit(target)
    }
  }, {
    root: root || null,
    rootMargin: profileMargin(profile, renderManaged),
    threshold: [0, 0.001],
  })

  const pool = {
    key,
    root: root || null,
    profile: profileName(profile),
    renderManaged: !!renderManaged,
    channel: renderManaged ? 'motion' : 'visual',
    observer,
    targets: new Set(),
  }
  observerPools.set(key, pool)
  return pool
}

function attachPool(record, pool, keyName, pendingKey) {
  if (!pool) return false
  pool.targets.add(record.node)
  record[keyName] = pool.key
  record[pendingKey] = true
  safeCall(() => pool.observer.observe(record.node))
  return true
}

function detachPool(record, keyName) {
  if (!record?.[keyName]) return
  const pool = observerPools.get(record[keyName])
  record[keyName] = ''
  if (!pool) return
  safeCall(() => pool.observer.unobserve(record.node))
  pool.targets.delete(record.node)
  cleanupPoolIfEmpty(pool)
}

function observeRecord(record) {
  record.firstIntersectionPending = false
  if (record.viewportPinned) {
    record.near = true
    record.poolKey = ''
    applyRecordState(record)
    return
  }

  const pool = getObserver(record.root, record.marginProfile, record.renderManaged)
  if (!pool) {
    record.near = true
    record.poolKey = ''
    applyRecordState(record)
    return
  }

  attachPool(record, pool, 'poolKey', 'firstIntersectionPending')
}

function unobserveRecord(record) {
  detachPool(record, 'poolKey')
}

function desiredState(record) {
  if (!record.node?.isConnected) return 'paused'
  if (!documentVisible) return 'paused'
  if (reducedMotion) return 'paused'
  // Conversion temporarily owns the motion budget. The shared progress status
  // remains running, including its subscribed percentage-smoothing RAF.
  if (videoHeavyActivityActive && !record.node.matches?.('.ql7PipelineProgress')) return 'paused'
  if (!record.enabled || !record.open || record.collapsed) return 'paused'
  // Physical iOS /exchange is a motion-continuity exception. WebKit async scrolling
  // becomes unstable when nested Exchange scopes are pause/resume-owned while the
  // finger is moving. Keep motion hot here without restoring the retired FULL/CHEAP
  // render governor, prepaint observer, filters, shadows or resource prewarm.
  if (isIOSExchangeMotionContinuityRuntime()) return 'running'
  if (record.viewportPinned) return 'running'
  if (!record.near) return 'paused'
  return 'running'
}

function isCssKeyframeAnimation(animation) {
  try {
    if (typeof CSSAnimation === 'function' && animation instanceof CSSAnimation) return true
  } catch {}
  return String(animation?.constructor?.name || '') === 'CSSAnimation'
}

function animationTarget(animation) {
  try {
    const target = animation?.effect?.target
    return target instanceof Element ? target : null
  } catch {
    return null
  }
}

function animationBelongsToRecord(animation, record) {
  const target = animationTarget(animation)
  if (!(target instanceof Element) || !(record?.node instanceof Element)) return false
  if (target === record.node) return true
  try { return target.closest?.('[data-ql7-visual-scope]') === record.node } catch { return false }
}

function collectAnimations(record, target = null) {
  const source = target instanceof Element ? target : record?.node
  if (!(source instanceof Element) || typeof source.getAnimations !== 'function') return []
  if (target instanceof Element) {
    counters.animationTargetReconciles += 1
    return safeCall(() => source.getAnimations()) || []
  }
  counters.scopedAnimationScans += 1
  return safeCall(() => source.getAnimations({ subtree: true })) || []
}

function mergeTracked(map, node, additions) {
  if (!additions.length) return
  const existing = map.get(node) || []
  const merged = [...existing]
  for (const animation of additions) if (!merged.includes(animation)) merged.push(animation)
  map.set(node, merged)
}

function cssMotionTargetBelongsToRecord(target, record) {
  if (!(target instanceof Element) || !(record?.node instanceof Element)) return false
  if (target === record.node) return true
  try { return target.closest?.('[data-ql7-visual-scope]') === record.node } catch { return false }
}

function writeCssMotionOwnerStopped(target, stopped) {
  if (!(target instanceof Element)) return false
  const currentlyStopped = target.getAttribute(CSS_MOTION_OWNER_STOP_ATTR) === '1'
  if (currentlyStopped === !!stopped) {
    counters.cssOwnerNoopWritesAvoided += 1
    return false
  }
  if (stopped) target.setAttribute(CSS_MOTION_OWNER_STOP_ATTR, '1')
  else target.removeAttribute(CSS_MOTION_OWNER_STOP_ATTR)
  return true
}

function forEachCssMotionTarget(record, callback) {
  if (!record?.cssMotionTargets) return
  for (const marker of record.cssMotionTargets) {
    const target = record.cssMotionUsesWeakRefs ? marker.deref() : marker
    if (!(target instanceof Element) || !cssMotionTargetBelongsToRecord(target, record)) {
      record.cssMotionTargets.delete(marker)
      if (target instanceof Element) writeCssMotionOwnerStopped(target, false)
      counters.cssOwnerPrunes += 1
      continue
    }
    callback(target)
  }
}

function registerCssMotionTarget(record, target) {
  if (!record?.pauseCss || !cssMotionTargetBelongsToRecord(target, record)) return false
  if (record.cssMotionTargetsSeen.has(target)) {
    if (record.cssMotionStopped) writeCssMotionOwnerStopped(target, true)
    return false
  }
  record.cssMotionTargetsSeen.add(target)
  record.cssMotionTargets.add(record.cssMotionUsesWeakRefs ? new WeakRef(target) : target)
  counters.cssOwnerRegistrations += 1
  if (record.cssMotionStopped) writeCssMotionOwnerStopped(target, true)
  return true
}

function setCssMotionStopped(record, stopped) {
  if (!record?.pauseCss || !(record.node instanceof Element)) {
    if (!stopped) {
      record?.node?.removeAttribute?.('data-ql7-css-motion')
      forEachCssMotionTarget(record, (target) => writeCssMotionOwnerStopped(target, false))
    }
    return
  }
  // Legacy scope-wide markers are deliberately inert. They invalidated every
  // descendant and pseudo-element whenever an observed surface crossed the
  // viewport margin. Concrete animation owners are discovered by the captured
  // animationstart event and retained weakly instead.
  record.node.removeAttribute('data-ql7-css-motion')
  const previous = record.cssMotionStopped
  record.cssMotionStopped = !!stopped
  if (stopped) {
    forEachCssMotionTarget(record, (target) => writeCssMotionOwnerStopped(target, true))
    if (!previous) {
      counters.cssStopped += 1
      if (record.renderManaged) counters.managedCssScanSkips += 1
    }
    return
  }
  forEachCssMotionTarget(record, (target) => writeCssMotionOwnerStopped(target, false))
  if (previous) counters.cssRestarted += 1
}

function pauseOwnedWaapi(record, target = null) {
  if (!record.pauseJs) return
  const owned = []
  for (const animation of collectAnimations(record, target)) {
    if (!animation || isCssKeyframeAnimation(animation) || animation.playState !== 'running') continue
    if (!animationBelongsToRecord(animation, record)) continue
    try {
      animation.pause()
      owned.push(animation)
      counters.runtimePaused += 1
    } catch {}
  }
  if (target instanceof Element) mergeTracked(runtimePausedWaapi, record.node, owned)
  else if (owned.length) runtimePausedWaapi.set(record.node, owned)
}

function resumeOwnedWaapi(record) {
  const owned = runtimePausedWaapi.get(record.node) || []
  runtimePausedWaapi.delete(record.node)
  for (const animation of owned) {
    try {
      if (animation.playState === 'paused') {
        animation.play()
        counters.runtimeResumed += 1
      }
    } catch {}
  }
}

function setLoopState(subscriber, next) {
  if (!subscriber?.loop || subscriber.loopState === next) return
  if (subscriber.loopState === 'running' && counters.loopRunning > 0) counters.loopRunning -= 1
  if (subscriber.loopState === 'paused' && counters.loopPaused > 0) counters.loopPaused -= 1
  subscriber.loopState = next
  if (next === 'running') counters.loopRunning += 1
  if (next === 'paused') counters.loopPaused += 1
}

function notifySubscribers(record, next) {
  for (const subscriber of record.subscribers) {
    setLoopState(subscriber, next)
    if (next === 'running') safeCall(subscriber.onRun, record.node)
    else safeCall(subscriber.onPause, record.node)
  }
}

function applyRecordState(record) {
  if (!record || records.get(record.node) !== record) return
  const next = desiredState(record)
  const nextMotionMode = reducedMotion || next !== 'running' ? 'stopped' : 'hot'
  const stateChanged = record.state !== next

  if (stateChanged && next === 'paused') {
    if (record.pauseJs) pauseOwnedWaapi(record)
  }
  if (stateChanged) record.state = next
  // `animation: none` destroys CSSAnimation timelines. Removing the attribute
  // recreates them from keyframe zero without retaining Animation wrappers.
  setCssMotionStopped(record, next !== 'running')

  // Render-managed scopes publish only motion lifecycle. Static paint, shadows,
  // filters and layer topology never change as the user scrolls.
  if (record.renderManaged) {
    const previousMotionMode = record.node.dataset.ql7MotionMode
    if (previousMotionMode !== nextMotionMode) {
      record.node.dataset.ql7MotionMode = nextMotionMode
      counters.motionModeWrites += 1
    }
  }

  if (record.publishState) {
    const previous = record.node.dataset.ql7VisualState
    if (previous !== next) {
      record.node.dataset.ql7VisualState = next
      counters.datasetWrites += 1
    }
  }

  if (!stateChanged) return
  if (next === 'running') {
    resumeOwnedWaapi(record)
  }
  notifySubscribers(record, next)
}

function normalizedOptions(node, options = {}) {
  const marginProfile = profileName(options.marginProfile)
  const root = options.rootStrategy === 'viewport'
    ? null
    : resolveMarkedScrollRoot(node, options.root)
  return {
    kind: String(options.kind || node?.dataset?.ql7VisualScope || 'scope'),
    marginProfile,
    root,
    rootStrategy: options.rootStrategy === 'viewport' ? 'viewport' : 'nearest-marker',
    enabled: options.enabled !== false,
    open: options.open !== false,
    collapsed: options.collapsed === true,
    pauseCss: options.pauseCss !== false,
    pauseJs: options.pauseJs === true,
    publishState: options.publishState === true,
    renderManaged: options.renderManaged === true,
    viewportPinned: options.viewportPinned === true,
    initialNear: options.initialNear !== false,
  }
}

export function registerVisualScope(node, options = {}) {
  if (!(node instanceof Element)) return () => {}
  const existing = records.get(node)
  if (existing) {
    // An explicit ref/hook registration is authoritative over discovery from
    // data-ql7-visual-scope. Global startup discovery must never downgrade an
    // already-mounted owner (publishState, initialNear, root or subscribers).
    datasetManagedScopes.delete(node)
    updateVisualScope(node, options)
    return () => unregisterVisualScope(node)
  }

  datasetManagedScopes.delete(node)
  const normalized = normalizedOptions(node, options)
  const record = {
    node,
    ...normalized,
    near: normalized.viewportPinned ? true : normalized.renderManaged ? false : normalized.initialNear,
    state: '',
    poolKey: '',
    firstIntersectionPending: false,
    subscribers: new Set(),
    cssMotionStopped: false,
    cssMotionUsesWeakRefs: typeof WeakRef === 'function',
    cssMotionTargets: new Set(),
    cssMotionTargetsSeen: new WeakSet(),
  }

  records.set(node, record)
  nodes.add(node)
  counters.registrations += 1
  if (!node.dataset.ql7VisualScope) node.dataset.ql7VisualScope = record.kind
  applyRecordState(record)
  observeRecord(record)
  for (const listener of scopeLifecycleListeners) safeCall(listener, { node, registered: true, initial: false })
  return () => unregisterVisualScope(node)
}

// Shared surface discovery, including dynamically paginated cards and ads.
// Consumers do not need a document MutationObserver or their own DOM census.
export function subscribeVisualScopeLifecycle(listener) {
  if (typeof listener !== 'function') return () => {}
  scopeLifecycleListeners.add(listener)
  for (const node of nodes) safeCall(listener, { node, registered: true, initial: true })
  return () => scopeLifecycleListeners.delete(listener)
}

export function updateVisualScope(node, patch = {}) {
  const record = records.get(node)
  if (!record) return registerVisualScope(node, patch)

  const next = normalizedOptions(node, {
    ...record,
    ...patch,
    initialNear: record.near,
  })
  const renderManagedChanged = next.renderManaged !== record.renderManaged
  const observerChanged = next.root !== record.root || next.marginProfile !== record.marginProfile || next.viewportPinned !== record.viewportPinned || renderManagedChanged
  if (observerChanged) unobserveRecord(record)
  if (renderManagedChanged && next.renderManaged && !next.viewportPinned) record.near = false
  Object.assign(record, next)
  if (observerChanged) observeRecord(record)
  applyRecordState(record)
  return () => unregisterVisualScope(node)
}

export function unregisterVisualScope(node) {
  const record = records.get(node)
  if (!record) return
  const timer = pendingExit.get(node)
  if (timer) clearTimeout(timer)
  pendingExit.delete(node)
  unobserveRecord(record)
  if (node?.isConnected) {
    if (record.renderManaged) {
      node.removeAttribute('data-ql7-motion-mode')
    }
    node.removeAttribute('data-ql7-css-motion')
    forEachCssMotionTarget(record, (target) => writeCssMotionOwnerStopped(target, false))
    resumeOwnedWaapi(record)
  } else {
    runtimePausedWaapi.delete(node)
  }
  for (const subscriber of record.subscribers) setLoopState(subscriber, '')
  record.subscribers.clear()
  datasetManagedScopes.delete(node)
  records.delete(node)
  nodes.delete(node)
  counters.unregistrations += 1
  for (const listener of scopeLifecycleListeners) safeCall(listener, { node, registered: false, initial: false })
}

export function subscribeVisualActivity(node, { onRun, onPause, loop = false } = {}) {
  if (!(node instanceof Element)) return () => {}
  let record = records.get(node)
  if (!record) {
    registerVisualScope(node, {
      kind: node.dataset.ql7VisualScope || 'visual-loop',
      marginProfile: node.dataset.ql7VisualMargin === 'near50' ? 'near50' : 'near100',
      rootStrategy: node.dataset.ql7VisualRoot === 'viewport' ? 'viewport' : 'nearest-marker',
      pauseCss: node.dataset.ql7VisualPauseCss !== '0',
      pauseJs: node.dataset.ql7VisualPauseJs === '1',
    })
    record = records.get(node)
  }

  const subscriber = { onRun, onPause, loop: loop === true, loopState: '' }
  if (subscriber.loop && [...record.subscribers].some((item) => item.loop)) counters.duplicateChains += 1
  record.subscribers.add(subscriber)
  const next = record.state === 'running' ? 'running' : 'paused'
  setLoopState(subscriber, next)
  if (next === 'running') safeCall(onRun, node)
  else safeCall(onPause, node)

  return () => {
    const current = records.get(node)
    if (!current?.subscribers?.has(subscriber)) return
    setLoopState(subscriber, '')
    current.subscribers.delete(subscriber)
  }
}

function datasetOptions(node) {
  // Dataset-only scopes deliberately keep lifecycle predicates out of the JS record.
  // open/collapsed/enabled are enforced directly by the global CSS selector so a
  // React attribute flip can resume before paint without a MutationObserver or a
  // second registry update. Explicit hook/subscription owners can still pass those
  // predicates through registerVisualScope/updateVisualScope when JS loop ownership
  // actually depends on them.
  return {
    kind: String(node.dataset.ql7VisualScope || 'scope'),
    marginProfile: node.dataset.ql7VisualMargin === 'near50' ? 'near50' : 'near100',
    rootStrategy: node.dataset.ql7VisualRoot === 'viewport' ? 'viewport' : 'nearest-marker',
    enabled: node.dataset.ql7VisualPinned === '1'
      ? node.dataset.ql7VisualPinnedActive !== '0'
      : true,
    open: true,
    collapsed: false,
    pauseCss: node.dataset.ql7VisualPauseCss !== '0',
    pauseJs: node.dataset.ql7VisualPauseJs === '1',
    publishState: false,
    renderManaged: node.dataset.ql7RenderManaged === '1',
    viewportPinned: node.dataset.ql7VisualPinned === '1',
  }
}

export function registerDatasetScope(node) {
  if (!(node instanceof Element) || !node.dataset.ql7VisualScope) return () => {}
  const existing = records.get(node)
  if (existing) {
    // Dataset discovery is subordinate to an explicit component/hook owner.
    // This is critical for hydration-safe ref-owned nodes: the root controller
    // may discover their structural scope after commit, but must not replace
    // publishState=true or initialNear=false with generic dataset defaults.
    if (datasetManagedScopes.has(node)) updateVisualScope(node, datasetOptions(node))
    return () => {}
  }
  const cleanup = registerVisualScope(node, datasetOptions(node))
  datasetManagedScopes.add(node)
  return () => {
    if (!datasetManagedScopes.has(node)) return
    datasetManagedScopes.delete(node)
    cleanup?.()
  }
}

export function refreshDatasetScope(node) {
  if (!(node instanceof Element) || !node.dataset.ql7VisualScope) return
  if (!datasetManagedScopes.has(node)) return
  updateVisualScope(node, datasetOptions(node))
}

export function registerNearestDatasetScope(target, { fromAnimationStart = false } = {}) {
  if (!(target instanceof Element)) return null
  const scope = target.closest?.('[data-ql7-visual-scope]')
  if (!(scope instanceof Element)) return null
  if (fromAnimationStart) counters.animationStartRegistrations += 1
  if (!records.has(scope)) registerDatasetScope(scope)
  return scope
}

export function reconcileVisualScopeAnimationTarget(scope, target) {
  const record = records.get(scope)
  if (!record || !(target instanceof Element)) return
  // Register every concrete CSSAnimation owner, including owners first seen while
  // HOT. A later viewport exit can then stop only those elements without a broad
  // descendant selector or an animation census.
  registerCssMotionTarget(record, target)
  if (desiredState(record) !== 'paused') return
  if (record.pauseCss) {
    setCssMotionStopped(record, true)
    if (record.renderManaged) counters.managedAnimationTargetScanSkips += 1
  }
  if (record.pauseJs) pauseOwnedWaapi(record, target)
}

export function noteStartupScopeScan(count) {
  counters.startupScopeScanTargets = Math.max(0, Number(count || 0))
}

function sweepDisconnected() {
  counters.disconnectedSweepRuns += 1
  // Set iteration tolerates deleting the current entry, so avoid allocating a
  // full [...nodes] copy on every fallback sweep. Explicit React ref owners still
  // unregister synchronously; this one-second pass only bounds stale dataset-only
  // scopes that disappeared without an owner cleanup callback.
  for (const node of nodes) {
    if (node?.isConnected) continue
    counters.disconnectedSweepReleases += 1
    unregisterVisualScope(node)
  }
}

export function installVisualRegistryDocumentController() {
  if (installed || typeof document === 'undefined') return () => {}
  installed = true
  documentVisible = document.visibilityState === 'visible'

  try {
    mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null
    reducedMotion = !!mediaQuery?.matches
  } catch {
    reducedMotion = false
  }

  visibilityListener = () => {
    documentVisible = document.visibilityState === 'visible'
    document.documentElement?.toggleAttribute?.('data-ql7-document-hidden', !documentVisible)
    for (const node of nodes) applyRecordState(records.get(node))
  }
  document.addEventListener('visibilitychange', visibilityListener, { passive: true })
  document.documentElement?.toggleAttribute?.('data-ql7-document-hidden', !documentVisible)

  videoHeavyActivityActive = isVideoHeavyActivityActive() && isMobileVideoMotionPauseRuntime()
  // Reconcile registered loop owners only at the begin/end boundary.
  // No document animation census, DOM discovery, new observer or RAF polling.
  const unsubscribeVideoHeavy = subscribeVideoHeavyActivity((active) => {
    const pauseMobileMotion = active && isMobileVideoMotionPauseRuntime()
    if (videoHeavyActivityActive === pauseMobileMotion) return
    videoHeavyActivityActive = pauseMobileMotion
    for (const node of nodes) applyRecordState(records.get(node))
  })

  mediaQueryListener = (event) => {
    reducedMotion = !!event?.matches
    for (const node of nodes) applyRecordState(records.get(node))
  }
  try { mediaQuery?.addEventListener?.('change', mediaQueryListener) } catch {}

  // Reconcile scopes that registered before the document controller effect.
  // This applies initial visibility/reduced-motion policy without publishing
  // attributes into generic React-owned markup.
  for (const node of nodes) applyRecordState(records.get(node))

  disconnectedSweepId = window.setInterval(sweepDisconnected, DISCONNECTED_SWEEP_MS)

  return () => {
    unsubscribeVideoHeavy()
    try { document.removeEventListener('visibilitychange', visibilityListener) } catch {}
    try { mediaQuery?.removeEventListener?.('change', mediaQueryListener) } catch {}
    if (disconnectedSweepId) clearInterval(disconnectedSweepId)
    disconnectedSweepId = 0
    mediaQuery = null
    mediaQueryListener = null
    visibilityListener = null
    installed = false
  }
}

function effectiveSnapshotState(record) {
  if (!record?.node?.isConnected) return 'paused'
  const ds = record.node.dataset || {}
  if (!documentVisible || reducedMotion) return 'paused'
  if (ds.ql7VisualOpen === '0' || ds.ql7VisualOpen === 'false') return 'paused'
  if (ds.ql7VisualCollapsed === '1' || ds.ql7VisualCollapsed === 'true') return 'paused'
  if (ds.ql7VisualEnabled === '0' || ds.ql7VisualEnabled === 'false') return 'paused'
  if (ds.ql7VisualPinned === '1' && ds.ql7VisualPinnedActive === '0') return 'paused'
  return record.state === 'running' ? 'running' : 'paused'
}

export function getVisualActivityFor(node) {
  const record = records.get(node)
  if (!record) return null
  return {
    kind: record.kind,
    state: effectiveSnapshotState(record),
    near: !!record.near,
    marginProfile: record.marginProfile,
    rootType: record.root ? 'marked-scroll-root' : 'viewport',
    connected: !!record.node?.isConnected,
    enabled: !!record.enabled,
    open: !!record.open,
    collapsed: !!record.collapsed,
    viewportPinned: !!record.viewportPinned,
    renderManaged: !!record.renderManaged,
    motionMode: record.node?.dataset?.ql7MotionMode || '',
  }
}

export function getVisualActivitySnapshot() {
  const byKind = {}
  let running = 0
  let paused = 0
  let disconnected = 0
  const motionModes = { hot: 0, warm: 0, stopped: 0 }
  for (const node of nodes) {
    const record = records.get(node)
    if (!record) continue
    if (!node.isConnected) disconnected += 1
    if (effectiveSnapshotState(record) === 'running') running += 1
    else paused += 1
    if (record.renderManaged) {
      const mode = record.node?.dataset?.ql7MotionMode
      if (mode && Object.prototype.hasOwnProperty.call(motionModes, mode)) motionModes[mode] += 1
    }
    byKind[record.kind] = (byKind[record.kind] || 0) + 1
  }

  return {
    scopes: {
      total: nodes.size,
      running,
      paused,
      disconnected,
      byKind,
      motionModes,
    },
    observers: {
      pools: observerPools.size,
      targets: [...observerPools.values()].reduce((sum, pool) => sum + pool.targets.size, 0),
      profiles: [...observerPools.values()].map((pool) => ({
        profile: pool.profile,
        rootType: pool.root ? 'marked-scroll-root' : 'viewport',
        targets: pool.targets.size,
        renderManaged: !!pool.renderManaged,
        channel: pool.channel,
      })),
    },
    animations: {
      runtimePaused: counters.runtimePaused,
      runtimeResumed: counters.runtimeResumed,
      cssStopped: counters.cssStopped,
      cssRestarted: counters.cssRestarted,
      cssOwnerRegistrations: counters.cssOwnerRegistrations,
      cssOwnerPrunes: counters.cssOwnerPrunes,
      cssOwnerNoopWritesAvoided: counters.cssOwnerNoopWritesAvoided,
    },
    loops: {
      running: counters.loopRunning,
      paused: counters.loopPaused,
      duplicateChains: counters.duplicateChains,
    },
    runtimeCostGuards: {
      startupScopeScanTargets: counters.startupScopeScanTargets,
      animationStartRegistrations: counters.animationStartRegistrations,
      observerCallbacks: counters.observerCallbacks,
      observerEntries: counters.observerEntries,
      datasetWrites: counters.datasetWrites,
      motionModeWrites: counters.motionModeWrites,
      forcedLayoutReads: counters.forcedLayoutReads,
      globalAnimationScans: counters.globalAnimationScans,
      bodyMutationObservers: counters.bodyMutationObservers,
      descendantStateScans: counters.descendantStateScans,
      scopedAnimationScans: counters.scopedAnimationScans,
      animationTargetReconciles: counters.animationTargetReconciles,
      managedCssScanSkips: counters.managedCssScanSkips,
      managedAnimationTargetScanSkips: counters.managedAnimationTargetScanSkips,
      cssOwnerNoopWritesAvoided: counters.cssOwnerNoopWritesAvoided,
      disconnectedSweepRuns: counters.disconnectedSweepRuns,
      disconnectedSweepReleases: counters.disconnectedSweepReleases,
      disconnectedObserverReleases: counters.disconnectedObserverReleases,
      disconnectedSweepMs: DISCONNECTED_SWEEP_MS,
    },
    lifecycle: {
      registrations: counters.registrations,
      unregistrations: counters.unregistrations,
      documentVisible,
      reducedMotion,
    },
  }
}

export function teardownVisualActivityRegistry() {
  if (disconnectedSweepId) clearInterval(disconnectedSweepId)
  disconnectedSweepId = 0
  for (const node of [...nodes]) unregisterVisualScope(node)
  for (const pool of observerPools.values()) safeCall(() => pool.observer.disconnect())
  observerPools.clear()
  try { document.documentElement?.removeAttribute?.('data-ql7-document-hidden') } catch {}
}
